import { Chip, Stack, Typography } from '@mui/material';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import timeSinceLastUpdate from '../../../../utils/timeSinceLastUpdate';

interface CollectionContentProps {
  collection: {
    subjects: string[];
    changed: string;
  };
}

const CollectionContent: React.FC<CollectionContentProps> = ({ collection }) => {
  const { subjects = [], changed } = collection;
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState<number>(subjects.length);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const { scrollWidth, clientWidth } = container;
      if (scrollWidth > clientWidth && visibleCount > 0) {
        setVisibleCount((prev) => prev - 1);
      }
    }, 0);

    const handleResize = () => {
      setVisibleCount(subjects.length);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleCount, subjects]);

  const hiddenCount = subjects.length - visibleCount;

  return (
    <>
      <Stack
        ref={containerRef}
        direction='row'
        spacing={spaces.xs}
        height={30}
        sx={{
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {subjects.slice(0, visibleCount).map((subject) => (
          <Chip
            key={subject}
            label={subject}
            variant='outlined'
            color='default'
            size='small'
            sx={{
              border: `1px solid ${palette.components.chip.defaultEnabled}`,
              height: 'auto',
              whiteSpace: 'nowrap',
            }}
          />
        ))}
        {hiddenCount > 0 && (
          <Chip
            label={`+ ${hiddenCount} ${t('Subject', { count: hiddenCount })}`}
            variant='outlined'
            color='default'
            size='small'
            sx={{
              border: 'none',
              height: 'auto',
              whiteSpace: 'nowrap',
              '& .MuiChip-label': { padding: 0 },
            }}
          />
        )}
      </Stack>
      <Typography variant='caption' sx={{ color: palette.text.primary }}>
        {timeSinceLastUpdate(changed)} {t('Updated (date)')}
      </Typography>
    </>
  );
};

export default memo(CollectionContent);

